/**
 * Phone helpers for tool-timeout-callback.tsx's call-back form. Deliberately
 * thin wrappers over lib/contact-form-validation.ts (the shared source of
 * truth for "is this phone shape plausible") rather than a second
 * implementation — see that module's own doc comment for why it's loose
 * (backend re-validates authoritatively).
 *
 * The call-back form needs a REQUIRED phone (unlike ContactInfoCard's
 * optional one) with its own gentle, non-red microcopy — "That looks a
 * couple of digits short - mind checking it?" — that the shared validator
 * doesn't know about (it returns a generic "Enter a valid phone number.").
 * `isDialablePhone` reuses the shared validator for the actual shape check
 * so the two forms can never silently disagree on what counts as valid; the
 * bespoke copy lives entirely in the component.
 */
import { validateContactForm } from './contact-form-validation';

/** True once `phoneNumber` passes the same shape check ContactInfoCard uses
 *  for a required phone field (non-empty, plausible character set, enough
 *  digits) — see contact-form-validation.ts's PHONE_CHARS_RE/MIN_PHONE_DIGITS.
 *  Does NOT consider the country code — the "Phone number" field is what the
 *  user is looking at when deciding whether to keep typing. */
export function isDialablePhone(phoneNumber: string): boolean {
  const errors = validateContactForm([{ name: 'phone', required: true }], {
    name: '',
    email: '',
    phone: phoneNumber,
  });
  return errors.phone === undefined;
}

/** Masks all but the last 4 digits of a phone number for the "details
 *  already on file" one-tap confirm card: "+1 555-123-4567" -> "...4567".
 *  Operates on digits only (formatting punctuation in the source string is
 *  discarded, not preserved-then-masked) — the design's "Call you on
 *  ...3210?" mock shows a plain digit run, not a re-punctuated one. Returns
 *  the digits as-is (no leading "...") if there are 4 or fewer — nothing
 *  meaningful to hide. */
export function maskPhoneLast4(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  return `...${digits.slice(-4)}`;
}
